import { useState, useMemo } from 'react'
import { Moment } from 'moment'
import clsx from 'clsx'
import {
  ArrowButton,
  Button,
  Card,
  CardProps,
  DatePicker,
  Icon,
  Popover,
  Row,
  Select,
  Space,
  Tag,
} from 'components/common'
import { LedgersTable, LedgersTableProps } from 'components/ledgers'
import { ellipsisText } from 'utils/custom-functions'

export interface DropDownItem {
  id: string
  title: string
  status: string
}

export interface LedgersCardProps
  extends CardProps,
    Pick<LedgersTableProps, 'dataSource' | 'openAddModal'> {
  date: Moment
  dropDownDefaultValue?: { title: string; status: string }
  handleViewDay: (day: Moment) => void
  handleDropDown: (title: string, status: string) => void
  subjects: DropDownItem[]
  loading: boolean
  subjectsLoading: boolean
}

export const LedgersCard = ({
  dataSource,
  date,
  dropDownDefaultValue,
  handleViewDay,
  handleDropDown,
  subjects,
  subjectsLoading,
  openAddModal,
  loading,
  ...rest
}: LedgersCardProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false)

  const options = useMemo(() => {
    const active = []
    const archived = []
    subjects?.forEach((subject, index) => {
      if (subject.status === 'ACTIVE') {
        active.push(
          <Select.Option
            key={`${index}${subject?.title}`}
            value={subject?.title}
          >
            {ellipsisText(subject?.title, 8).text}
          </Select.Option>
        )
      } else {
        archived.push(
          <Select.Option
            key={`${index}${subject?.title}`}
            value={subject?.title}
          >
            <Row>
              <Space>
                <div>{ellipsisText(subject?.title, 8).text}</div>
                <div className={clsx('flex', 'justify-end')}>
                  <Tag color={'gray'}>アーカイブ済み</Tag>
                </div>
              </Space>
            </Row>
          </Select.Option>
        )
      }
    })
    return [...active, ...archived]
  }, [subjects])

  const handleDropDownChange = (value: string) => {
    handleDropDown(value, dropDownDefaultValue?.status)
  }
  const handleDateChange = (date: Moment, dateString: string) => {
    handleViewDay(date)
    setIsVisible(false)
  }

  const handleUpDownMonth = (step: boolean) => {
    if (step) {
      handleViewDay(date.add(1, 'month').clone())
    } else {
      handleViewDay(date.subtract(1, 'month').clone())
    }
  }
  const buttonCover = useMemo((): JSX.Element => {
    return (
      <div className="space-x-2">
        <Icon.CalendarTwoTone />
        <span>{date.format('YYYY年MM月')}</span>
      </div>
    )
  }, [date])

  return (
    <Card
      loading={loading}
      title={
        <div className="flex">
          <Button.Group>
            <ArrowButton
              direction="left"
              onClick={() => handleUpDownMonth(false)}
            />

            <Popover
              title={'月を選択'}
              visible={isVisible}
              onVisibleChange={setIsVisible}
              trigger="click"
              content={
                <DatePicker
                  picker="month"
                  value={date}
                  onChange={handleDateChange}
                />
              }
            >
              <Button onClick={() => setIsVisible(!isVisible)}>
                {buttonCover}
              </Button>
            </Popover>

            <ArrowButton
              direction="right"
              onClick={() => handleUpDownMonth(true)}
            />
          </Button.Group>

          <div className="ml-4">
            <Select
              placeholder="項目を選択してください"
              style={{ width: 250 }}
              onChange={handleDropDownChange}
              defaultValue={dropDownDefaultValue.title || undefined}
              value={dropDownDefaultValue.title || undefined}
              loading={subjectsLoading}
            >
              {options}
            </Select>
          </div>
        </div>
      }
    >
      <LedgersTable
        loading={loading}
        dataSource={dataSource}
        openAddModal={openAddModal}
        itemStatus={dropDownDefaultValue?.status}
        subjectsDataLength={subjects?.length}
        pagination={false}
      />
    </Card>
  )
}
